import type { EarningsBreakdownRow } from './earnings-buckets';
import { PAYOUT_DEDUCTIONS_DEFAULT, type PayoutDeductionsConfig } from './payout-deductions';
import {
  ECOM_PAYMENT_METHODS_DEFAULT,
  type EcomPaymentMethodsConfig,
} from './ecom-payment-methods';
import {
  POS_PAYMENT_METHODS_DEFAULT,
  type PosPaymentMethodsConfig,
} from './pos-payment-methods';
import {
  MERCHANT_REQUIREMENTS_DEFAULT,
  type MerchantPayoutSchedule,
  type MerchantRequirementDef,
} from './merchant';
import { STOREFRONT_DEFAULT, resolveStorefront, type StorefrontConfig } from './storefront-content';
import type { AssistantPersonaOverride } from './assistant/personas';

/**
 * Cash-out availability window. When `enabled`, a member may only submit a
 * payout request on a day in `daysOfWeek` (0=Sun..6=Sat) and at a local hour in
 * `[openHour, closeHour)` (24h, tenant timezone). Disabled = always open.
 */
export type CashoutSchedule = {
  enabled: boolean;
  daysOfWeek: number[];
  openHour: number;
  closeHour: number;
};

/**
 * Is the cash-out window open at `nowLocalHour`/`nowLocalDay` (already resolved
 * to the tenant timezone)? Disabled schedules are always open. Pure so the
 * server gate and the member SPA can share one source of truth.
 */
export function isCashoutOpen(
  schedule: CashoutSchedule | null | undefined,
  nowLocalDay: number,
  nowLocalHour: number,
): boolean {
  if (!schedule || !schedule.enabled) return true;
  const days = Array.isArray(schedule.daysOfWeek) ? schedule.daysOfWeek : [];
  if (days.length > 0 && !days.includes(nowLocalDay)) return false;
  return nowLocalHour >= schedule.openHour && nowLocalHour < schedule.closeHour;
}

export const CASHOUT_SCHEDULE_DEFAULT: CashoutSchedule = {
  enabled: false,
  daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
  openHour: 0,
  closeHour: 24,
};

/**
 * Min/max bounds on cash-out requests, enforced in `POST /payouts/requests`.
 * `perSlot*` bound a SINGLE request's amount. `perMember*` bound the member
 * across ALL their slots: `perMemberMax` caps the sum of the member's
 * outstanding (requested/queued/processing) requests + the new one. `null` =
 * no bound. The effective single-request minimum is `max(perSlotMin,
 * perMemberMin)`.
 */
export type CashoutLimits = {
  perSlotMin: number | null;
  perSlotMax: number | null;
  perMemberMin: number | null;
  perMemberMax: number | null;
};

export const CASHOUT_LIMITS_DEFAULT: CashoutLimits = {
  perSlotMin: null,
  perSlotMax: null,
  perMemberMin: null,
  perMemberMax: null,
};

/**
 * Tenant-level constants for the payout batch disbursement file (the sheet
 * finance uploads to the bank). The per-member columns come off the request;
 * these are the ones that are the same on every row.
 *
 * `sender_mobile` / `sender_address` / `purpose` map 1:1 to the remittance-only
 * columns in the bank's template — blank for straight bank transfers, required
 * by some over-the-counter remittance processors.
 */
export type PayoutExportConfig = {
  /** Prefix on the generated transaction reference (`PE20260805000001`). */
  txn_prefix: string;
  /** Description column. Blank falls back to the payout method's label. */
  description: string;
  sender_mobile: string;
  sender_address: string;
  purpose: string;
};

export const PAYOUT_EXPORT_DEFAULT: PayoutExportConfig = {
  txn_prefix: 'PE',
  description: '',
  sender_mobile: '',
  sender_address: '',
  purpose: '',
};

/**
 * An admin-defined extra field collected on member signup, beyond the built-in
 * name/email/phone/etc. `type='text'` is a free-text input; `type='file'` is a
 * scanned upload (the signup form pre-uploads to a temp key and the value is
 * stored as `{ r2Key, filename }` on `users.registration_data[id]`). `id` is a
 * stable slug used as the storage key; `order` controls display order.
 */
export type RegistrationFieldType = 'text' | 'file';
export type RegistrationField = {
  id: string;
  label: string;
  type: RegistrationFieldType;
  required: boolean;
  order: number;
};

/**
 * Which fields a MEMBER may edit on their own Profile tab. Each flag gates both
 * the input (member SPA disables it) AND the server (`PATCH /me` rejects a
 * locked field). `name` governs first/middle/last together. Email, username,
 * and address are intentionally NOT here: email & username are permanently
 * read-only identity, and address has its own dedicated Address tab / checkout
 * editor (the Profile tab only shows it read-only). All default to editable so
 * an un-configured tenant keeps today's behavior.
 */
export type MemberEditableFields = {
  name: boolean;
  phone: boolean;
  profilePicture: boolean;
};
export const MEMBER_EDITABLE_FIELDS_DEFAULT: MemberEditableFields = {
  name: true,
  phone: true,
  profilePicture: true,
};

/**
 * Which detail fields show on each box (slot node) of the BINARY genealogy tree,
 * in both the member Genealogy page and the admin slot-detail Genealogy tab (the
 * tree component is shared). A display control only — the data is always fetched
 * and hidden client-side (like all other display flags), not a hard security
 * boundary. `fullName`/`slotCode` default ON (today's node card always shows the
 * owner + slot code); the binary points + sponsor fields default OFF (opt-in,
 * matching {@link show_sponsor_on_dashboard}). Rank / package / currency are not
 * configurable here and always show.
 */
export type BinaryGenealogyNodeFields = {
  fullName: boolean;
  slotCode: boolean;
  /** The membership/kit package amount (e.g. ₱23,400.00) + its currency unit. */
  packageAmount: boolean;
  leftPoints: boolean;
  rightPoints: boolean;
  sponsorName: boolean;
  sponsorSlotCode: boolean;
  /** The slot's current CASH wallet balance (slot_wallets.available). */
  walletBalance: boolean;
};
export const BINARY_GENEALOGY_NODE_FIELDS_DEFAULT: BinaryGenealogyNodeFields = {
  fullName: true,
  slotCode: true,
  // Off by default: the package amount is the same for everyone in a tier
  // (the kit price), so it reads as noise on the tree. Re-enable per tenant.
  packageAmount: false,
  leftPoints: false,
  rightPoints: false,
  sponsorName: false,
  sponsorSlotCode: false,
  // Off by default: a downline's wallet balance is sensitive — the tree is
  // visible to members, so exposing it is an explicit per-tenant decision.
  walletBalance: false,
};

/**
 * Which fields a MERCHANT may edit on their own Profile page. Same enforcement
 * posture as {@link MemberEditableFields} (input disabled + `PATCH /merchant/me`
 * rejects a locked field). `ownerName` edits the linked account's display name
 * (`users.full_name`). The account login email & username are permanently
 * read-only and not represented here. `pickupAddress` covers the pickup line /
 * city / region (the shipping origin) as one group. All default to editable.
 */
export type MerchantEditableFields = {
  businessName: boolean;
  ownerName: boolean;
  contactNumber: boolean;
  contactEmail: boolean;
  pickupAddress: boolean;
};
export const MERCHANT_EDITABLE_FIELDS_DEFAULT: MerchantEditableFields = {
  businessName: true,
  ownerName: true,
  contactNumber: true,
  contactEmail: true,
  pickupAddress: true,
};

/**
 * Admin-defined KYC document requirement. The member's KYC tab renders one
 * upload slot per entry (in order); each uploaded file's `key` is stored as the
 * `kind` on `kyc_submissions.documents[]`. Admins manage the list from the
 * Cash-out settings. All entries are image uploads.
 */
export type KycRequirementDef = {
  /** Stable slug stored on `kyc_submissions.documents[].kind`. */
  key: string;
  label: string;
  /** Help text shown under the upload slot. */
  description?: string;
  /** When false, the member may submit without it. */
  required: boolean;
};

/** Starter set mirroring the previously hardcoded slots; admins can edit it. */
export const KYC_REQUIREMENTS_DEFAULT: KycRequirementDef[] = [
  { key: 'gov_id_front', label: 'Government ID — front', required: true },
  { key: 'gov_id_back', label: 'Government ID — back', required: true },
  { key: 'selfie', label: 'Selfie holding your ID', required: false },
];

/**
 * One named group of plans that share a combined daily cash-income cap.
 * `id` is a stable UUID (the per-tier override and UI key on it, never the
 * mutable `label`). `limit` is the shared daily ceiling; `<= 0` = no cap.
 */
export type DailyCapGroup = {
  id: string;
  label: string;
  limit: number;
  plans: string[];
};

/**
 * One wallet top-up bonus bracket. When an approved top-up's amount falls in
 * [minAmount, maxAmount] (both INCLUSIVE; maxAmount null = open-ended top
 * bracket), the member receives a bonus credit. `bonusKind` decides BOTH the
 * math (percentage of the top-up amount vs a fixed amount) AND the destination
 * wallet (cash vs gc) — independent of the kind being topped up, so "top up
 * cash, get a GC bonus" is allowed. Money fields are decimal strings.
 *
 * Brackets are matched in array order: the FIRST match wins (overlaps are NOT
 * summed). Admins arrange the order in Settings.
 */
export type WalletTopupBracket = {
  id: string;
  minAmount: string;
  maxAmount: string | null;
  bonusKind: 'pct_wallet' | 'pct_gc' | 'fixed_wallet' | 'fixed_gc';
  /** Percent (e.g. "2" = 2%) for pct_*, or a fixed money amount for fixed_*. */
  value: string;
};

/** How a top-up bonus is handled when the top-up is paying down a CD debt. */
export const CD_TOPUP_BONUS_TREATMENTS = ['principal_only', 'include_cash_bonus', 'no_bonus'] as const;
export type CdTopupBonusTreatment = (typeof CD_TOPUP_BONUS_TREATMENTS)[number];

/** Which slot's price a member holding several slots pays. */
export const PRICING_SLOT_SOURCES = ['active_slot', 'best_across_slots'] as const;
export type PricingSlotSource = (typeof PRICING_SLOT_SOURCES)[number];

/**
 * Strongly-typed view of `tenant_config.features` (jsonb in DB). New keys land
 * here as they're added across phases; existing routes/services read them via
 * `getFeatures(db)` so a missing key gives the documented default, never
 * `undefined`.
 *
 * Adding a new key:
 *   1. Add the field + default below.
 *   2. Read it via `getFeatures(db).<key>` — never index the raw jsonb.
 *   3. If admins should be able to toggle it, expose it through
 *      `GET/PATCH /admin/tenant/features` (super_admin gated).
 */
export type TenantFeatures = {
  /**
   * If true, kit_codes issued from then on may be redeemed by any user (gift
   * card behavior). If false, only the original buyer may redeem. Existing
   * codes keep their issuance-time semantics — flipping this never
   * retroactively locks already-issued codes.
   */
  kit_codes_transferable: boolean;

  /**
   * Number of days a newly-issued kit code stays valid. `null` = never
   * expires. Like transferability, this is a snapshot taken at issuance.
   */
  kit_codes_expire_days: number | null;

  /**
   * If true, a member may transfer an unredeemed code from their own Code Vault
   * to a member in their downline (identified by username + email). The code
   * leaves the sender's vault and appears in the recipient's. Off by default.
   */
  kit_codes_downline_transfer: boolean;

  /**
   * F5: auto-create (auto-redeem) the slot when a Membership Kit is purchased,
   * per sales channel. When on for a channel, each freshly-minted activation code
   * from that channel's order is immediately redeemed for the buyer — sponsor +
   * binary placement resolved from registration (as a no-anchor redeem),
   * activation terms auto-accepted, no manual Code+PIN Vault step. Upgrade-only
   * codes are left for a manual upgrade; extra codes past the member's slot cap
   * stay redeemable in the Vault. Both channels default off.
   */
  kit_auto_create_slot: { ecom: boolean; cashier: boolean };

  /**
   * If true, members must have TOTP enrolled. `/auth/login` returns
   * `totp_setup_required` for members who don't yet have it. Admin + cashier
   * 2FA is always required regardless of this flag.
   */
  member_2fa_required: boolean;

  /**
   * Override for what triggers `order_completed` events on ecom orders.
   *   `payment_approved` (default) — fires the moment manual-payment is
   *      approved. Commissions land immediately.
   *   `order_completed` — fires only when fulfillment marks the order
   *      `completed`. Pushes commissions later but matches physical delivery.
   * POS orders always fire on checkout (in-person hand-off).
   */
  ecom_event_on: 'payment_approved' | 'order_completed';

  /**
   * When the Kit Code+PIN becomes visible to the buyer in their Vault for an
   * ecom kit order. Earlier reveal = faster member activation, but redeeming
   * the code marks that line item non-refundable even if the kit hasn't
   * physically arrived yet.
   *
   *   `payment_approved` — codes unlock the moment payment is approved (`paid`)
   *   `shipped`          — codes unlock when admin marks the order `shipping`
   *   `delivered`        — codes unlock when admin marks the order `delivered`
   *   `completed`        — codes unlock only after the member confirms receipt
   *                        (default; preserves the original behavior)
   * Wallet-instant ecom orders (pickup / address-less, or when
   * `wallet_orders_charge_shipping` is off) always mint codes immediately at
   * checkout, regardless of this setting. A wallet order that walks the ladder
   * (address + `wallet_orders_charge_shipping` on) instead honours this setting
   * like any other ecom order.
   */
  kit_code_reveal_on: 'payment_approved' | 'shipped' | 'delivered' | 'completed';

  /**
   * Number of days an order may sit awaiting the member's receipt confirmation
   * (`delivered` for shipping, `to_pick` for pickup) before the worker
   * auto-completes it. `null` or `0` disables the sweep (orders wait forever for
   * a manual "Mark received"). Default 7. Drives the periodic auto-complete job
   * in `apps/worker`; admin-adjustable in Tenant Settings.
   */
  order_auto_complete_days: number | null;

  /**
   * When true (default), an ecom order whose every line is a VIRTUAL product
   * completes the moment payment lands — there is nothing to pick, pack, ship,
   * or hand over, so parking it at `paid` only makes an admin click twice.
   *
   * Deliberately narrow. It fires ONLY for an order stamped
   * `metadata.virtual_only` at creation (see `createOrderInTx`), so:
   *  - a MIXED cart (virtual + physical) is one order and walks the full ladder;
   *  - a branch-PICKUP order is untouched even though it also has no ship-to
   *    address — the marker is what an address-based check would get wrong;
   *  - a COD-Manual order never reaches `paid` at all, so nobody is credited
   *    for goods that haven't been paid for.
   * POS is untouched (it already completes at checkout, or parks at
   * `to_release` under `branches.completion_mode = 'manual'`).
   *
   * Two knock-on effects, both intended: under `ecom_event_on='order_completed'`
   * commissions on an all-virtual order now land at payment instead of waiting
   * for an admin, and under `kit_code_reveal_on='completed'` a virtual kit's
   * Code+PIN unlocks at payment (which also makes that line non-refundable
   * earlier). Turn this off to keep the manual review step.
   */
  virtual_orders_auto_complete: boolean;

  /**
   * When true, a MIXED ecom cart (virtual + physical lines) is split by the
   * server into TWO orders sharing one `checkout_group_id`: a physical order
   * that walks the normal shipping ladder, and a virtual order that carries no
   * address, no courier and no fee, is stamped `metadata.virtual_only`, and so
   * completes on payment via `virtual_orders_auto_complete`. Default OFF — the
   * cart stays one order exactly as before.
   *
   * The point is delivery independence: today a buyer who adds a course and a
   * t-shirt to one cart waits for the t-shirt to ship before the course is
   * released. One cart, one payment, two fulfilment tracks.
   *
   * Deliberate boundaries:
   *  - An all-virtual or all-physical cart is NEVER split (there is no second
   *    half to make), so nothing changes for a single-kind cart.
   *  - COD-Manual is excluded: cash is collected once on hand-over, and a
   *    virtual half has nothing to hand over — it would strand on the ladder
   *    (`virtual-auto-complete.ts` excludes COD for the same reason).
   *  - The coupon rides EXACTLY ONE half (`chooseCouponHalf`): a redemption
   *    burns a use per order, so passing the code to both would fail the second
   *    with `coupon_exhausted`.
   *  - Only the physical half receives shipping args; the buyer pays once via
   *    the existing group-payment machinery (`submitManualPaymentGroup`, group
   *    gateway initiate, the member `OrderGroupCard`).
   */
  virtual_orders_split_checkout: boolean;

  /**
   * Phase 13.5: list of `plan_code`s the tenant doesn't use. Disabled plans
   * are skipped by the worker before `runEvent()` and hidden from the admin
   * Distribute / Distributions / ComPlan editor UIs. Plans not listed here
   * are active. Removing a code from this list re-enables the plan
   * immediately on the next event.
   *
   * Example: a tenant who doesn't run a vortex sign-up bonus can set
   * `["sign_up_vortex", "mentor_bonus"]` to silence them without deleting
   * their configs.
   */
  disabled_plans: string[];

  /**
   * Display order for the member "Earnings Breakdown" panel: an ordered list of
   * ledger `source_plan` codes. The panel groups the member's lifetime credits
   * by source_plan; codes listed here render first in this order, any earning
   * plan not listed renders after (sorted by amount). Disabled plans never show
   * regardless. Admin-arranged in Settings; empty = amount-sorted default.
   *
   * @deprecated Superseded by `earnings_breakdown_rows` (order + per-row
   * visibility mode). Kept as a read fallback only: `resolveEarningsBreakdownRows`
   * consults this when the new key is empty, so legacy tenants keep their
   * arrangement with zero data rewrite. The Settings editor writes the new key.
   */
  earnings_breakdown_order: string[];

  /**
   * Member "Earnings Breakdown" row config: array order = display order, one
   * entry per bucket key — a ledger `source_plan` code, or a sub-bucket key
   * from `EARNINGS_SUB_BUCKETS` (e.g. the Monthly Direct mentor rider split).
   * Per-row `mode`: `always` shows the row even at zero earnings,
   * `when_earned` (the default) only when it has credits, `hidden` suppresses
   * an active plan's row. Labels are NOT stored here — rows read/write the
   * shared `ledger_source_plan_labels` / `ledger_labels` maps, so a rename
   * follows everywhere (passbook, notifications, admin views). Empty = fall
   * back to the deprecated `earnings_breakdown_order`, then amount-sorted.
   */
  earnings_breakdown_rows: EarningsBreakdownRow[];

  /**
   * Member wallet top-ups: a member submits an amount + proof-of-payment image,
   * an admin approves it, and the slot wallet is credited. OFF by default.
   */
  wallet_topup_enabled: boolean;
  /** Bonus brackets applied on top-up approval (first matching bracket wins).
   *  Members pay via the same MANUAL `ecom_payment_methods` they use at checkout
   *  (GCash, bank transfer, …) — there is no separate top-up method list. */
  wallet_topup_brackets: WalletTopupBracket[];

  /**
   * Plans paid EXCLUSIVELY via an ad-hoc distribution run (admin-chosen date
   * window), never at the monthly `payout_period_closed`. A plan listed here is
   * skipped by the worker on `payout_period_closed` ONLY — its realtime
   * accumulation events (e.g. Binary leg banking on `slot_placed`/
   * `order_completed`) still run. Without this, a plan that handles BOTH the
   * period close AND a distribution run (e.g. `monthly_direct_bonus`, `binary`)
   * double-pays an overlapping window: the two paths use different `event_id`s,
   * so the ledger's per-event idempotency never dedupes them. Default empty
   * (no change in behavior); a tenant that distributes these ad-hoc should add
   * them here so the period close doesn't also pay them.
   */
  distribution_run_only_plans: string[];

  /**
   * Plan codes for which COMPANY slots are "auto-maintained": exempt from that
   * plan's per-period personal-PV (maintenance) gate. A company slot listed for
   * a plan always passes that plan's PPV/maintenance requirement regardless of
   * its actual repurchase; a plan NOT listed treats company slots like any other
   * slot. Only plans that HAVE a maintenance gate are meaningful here
   * (`stairstep`, `unilevel`, `maintenance_cashback`, `monthly_direct_bonus`).
   * Defaults to `['stairstep','unilevel']` — the two plans that were previously
   * hard-coded to always exempt company slots — so behavior is unchanged until
   * an admin edits the list.
   */
  company_auto_maintained_plans: string[];

  /**
   * Phase A (spec conformance): GLOBAL per-plan daily cash-income cap, keyed by
   * `plan_code`. The framework clamps a plan's daily cash credits per receiver
   * slot to this amount. A per-membership-tier override
   * (`memberships.config.daily_caps[plan_code]`) takes precedence when present;
   * the effective cap is `tierOverride ?? global ?? unlimited`. A missing key
   * or `0` means NO LIMIT. Binary is exempt (it self-caps via
   * `binary_daily_state` / `daily_income_limit`).
   *
   * Example: `{ "direct": 5000, "indirect": 2000 }`.
   */
  daily_caps: Record<string, number>;

  /**
   * If true, slot creation auto-places in the binary tree via spillover under
   * the sponsor when no explicit placement payload is given. If false
   * (default), slots are created unplaced and must be placed via
   * `POST /slots/:id/place` — the owner picks an exact L/R seat. Explicit
   * placement requests in the create body always honor the seat regardless
   * of this flag.
   */
  auto_place_slots: boolean;

  /**
   * When `true` (opt-in), the member redeem/activation form shows the "Place in
   * my binary tree now (otherwise place it later)" checkbox + anchor/side picker
   * (only relevant once the member already has ≥1 active slot). When `false`
   * (default), the checkbox is hidden and placement falls through to the server
   * default (no explicit anchor → `auto_place_slots` decides auto-spillover vs
   * unplaced). UI-only — server placement logic is unchanged.
   */
  manual_placement_on_redeem: boolean;

  /**
   * When `true` (default), the genealogy tree shows the "Activate New Slot" /
   * "Add New Slot" action card. When `false`, that entry point is hidden — the
   * Code Vault redeem flow still works. UI gate only.
   */
  genealogy_activate_slot: boolean;

  /**
   * When `true` (default), a `slot_type='free'` slot EARNS normally — it
   * receives commissions from its downlines like any paid slot. When `false`,
   * `runEvent` drops any proposed ledger credit whose recipient is a free slot
   * (the historical BF2 behavior). Either way, CREATING a free slot still fires
   * no upline comp (redeem suppresses `slot_activated`/`slot_placed` for free
   * slots) — this flag only governs whether a free slot can RECEIVE earnings.
   */
  free_slots_earn: boolean;

  /**
   * Replicated-link packages, per membership. Master show/hide for the whole
   * feature (default `true`). When `true`: the member Invite page shows the
   * "Package links" card, and a still-slotless lead is SCOPED to the activation
   * kits an admin tagged to their sponsor slot's membership
   * (`memberships.config.replicated_link_packages`) — the storefront/checkout/
   * dashboard hide OTHER memberships' kits while keeping every regular (non-kit)
   * product available. A membership with no tags (empty/absent) is unrestricted,
   * so the flag is a no-op until at least one tier is tagged. When `false`: the
   * Invite card is hidden and no kit scoping is applied (all kits available).
   * Scope derives live from `users.registered_under_slot_id` (no per-package URL,
   * no DB column) and lifts automatically once the lead activates a slot.
   * Resolved by `resolveLinkKitScope`, enforced in `listStorefrontProducts` +
   * re-validated at checkout (`createOrderInTx`).
   */
  package_replicated_link_enabled: boolean;

  /**
   * Member dashboard "Total saved" card (default `false`). When `true`, the
   * dashboard shows the lifetime sum of every discount the member received
   * across their online-store (ecom) and cashier (POS) purchases: per-product
   * membership/rank/stockist price discounts (`orders.discount_total`),
   * coupons (`orders.coupon_total`), and POS manager discounts
   * (`orders.manager_discount_amount`). Display-only — no comp effect. Cash
   * orders only (non-cash carries no discounts); legacy-imported and
   * cancelled/refunded/never-paid orders excluded.
   */
  total_saved_counter_enabled: boolean;

  /**
   * CD/Half-CD: when `true` (default), an IN-DEBT cd/half_cd slot's own
   * purchases fire `order_completed` comp for its uplines like any slot. When
   * `false`, the worker skips the commission plans for orders bought by an
   * in-debt CD slot (or in-debt Half-CD while `half_cd_triggers_like_ps` is
   * off) but still runs the PV rollup + live rank-up — volume flows, money
   * doesn't. Skipped commissions (incl. binary leg points from that order) are
   * NOT re-fired at settlement.
   */
  cd_orders_pay_upline: boolean;

  /**
   * CD: when `true` (default), an in-debt `cd` slot counts toward its
   * sponsor's Stairstep leg requirements (`min_directs` / `min_legs`) like any
   * active slot. When `false`, it only counts after its debt is settled (the
   * slot flips to `paid`). GPV contribution is untouched either way.
   */
  cd_stairstep_count_immediately: boolean;

  /** Same as `cd_stairstep_count_immediately`, but for in-debt `half_cd` slots. */
  half_cd_stairstep_count_immediately: boolean;

  /**
   * CD: when `true` (default), an in-debt `cd`/`half_cd` direct counts toward
   * its mentor's **Binary Mentor's Bonus** per-level `min-directs` requirement
   * like any active direct. When `false`, in-debt CD/Half-CD directs are
   * ignored for that requirement (they count again once settled → `paid`).
   * Mirrors the Stairstep leg gating; applies to the binary plan's mentor rider
   * (legacy/extreme + teslab variants), not the standalone per-tier mentor bonus.
   */
  cd_binary_mentor_count_directs: boolean;

  /**
   * CD: when `true` (default), an in-debt `cd`/`half_cd` direct counts toward
   * the **Extreme Binary preferred-placement (side) direct requirement** — the
   * gate that unlocks changing a slot's L/R spillover side. When `false`,
   * in-debt CD/Half-CD directs don't count until settled (`paid`).
   */
  cd_binary_placement_count_directs: boolean;

  /**
   * Half-CD: when `true`, redeeming a half_cd kit code fires entry comp
   * (`slot_activated`/`slot_placed`) immediately like a paid slot — uplines
   * are paid upfront and settlement then fires NOTHING (it only stops the
   * garnish). When `false` (default), half_cd defers entry comp to settlement
   * exactly like `cd`. Snapshotted onto `slot_debts.entry_comp_fired` at
   * redemption, so flipping the toggle later never double-fires.
   */
  half_cd_triggers_like_ps: boolean;

  /**
   * Half-CD: plan codes (`sourcePlan`, e.g. 'binary', 'unilevel') whose CASH
   * earnings are garnished into an in-debt half_cd slot's debt. Earnings from
   * plans NOT in this list credit the wallet normally. Empty (default) = a
   * half_cd slot's debt is only payable via admin adjustment. `cd` slots
   * ignore this list — every plan garnishes them.
   */
  half_cd_debt_plans: string[];

  /**
   * CD: when `true` (default), a cash payout request from a slot whose
   * `slot_type` is `'cd'` with an OPEN debt is rejected (`slot_in_debt`) — a
   * `cd` slot's cash must garnish into the debt, not cash out. `half_cd` slots
   * are ALWAYS allowed to cash out (their debt only garnishes `half_cd_debt_plans`
   * earnings, so other cash is legitimately withdrawable). A settled slot is
   * already `slot_type='paid'` and is never blocked. When `false`, in-debt `cd`
   * slots may cash out any materialized balance.
   */
  cd_block_payout_in_debt: boolean;

  /**
   * CD/Half-CD: when `true` (default), an approved member top-up to an in-debt
   * cd/half_cd slot is GARNISHED into that slot's debt (the deposited cash pays
   * the debt first, exactly like the slot's earnings do; any amount above the
   * remaining debt lands in the wallet). This is the member's self-serve payoff
   * — it garnishes BOTH cd and half_cd regardless of `half_cd_debt_plans` (that
   * list governs which EARNINGS garnish, not the member's own payment). When
   * `false`, top-ups always credit the wallet, even for an in-debt slot.
   */
  cd_topup_settles_debt: boolean;

  /**
   * How a top-up's BONUS (from `wallet_topup_brackets`) is treated when the
   * top-up is paying down a CD debt (only relevant while `cd_topup_settles_debt`
   * is on and the slot is in debt):
   *  - `principal_only` (default): only the deposited cash pays the debt; any
   *    cash/GC bonus credits the wallet as usual.
   *  - `include_cash_bonus`: the deposited cash AND any CASH bonus garnish into
   *    the debt (GC bonus can't — the debt is cash-only).
   *  - `no_bonus`: no bonus is granted at all on a debt-paying top-up.
   */
  cd_topup_bonus_treatment: CdTopupBonusTreatment;

  /**
   * Maximum number of slots a single member may own. `null` (default) =
   * unlimited. Enforced at slot creation (`createSlot`); the member area shows
   * `current_slot_count / max_slots_per_member`.
   */
  max_slots_per_member: number | null;

  /**
   * Slot-code series numbering. When `enabled`, a new slot's code keeps the
   * usual random base (e.g. "SK8N3PQRX7M") and gets a per-member ordinal suffix
   * ("-01", "-02", …) reflecting how many slots the member already has. Off by
   * default; only affects slots created after enabling (no retro-renumber).
   */
  slot_code_series: { enabled: boolean };

  /**
   * BF2: when true, a paid->paid slot upgrade (tier bump on an already-earning
   * slot) RE-FIRES activation comp (sign-up bonuses etc.) for the new tier.
   * When false (default), a paid->paid upgrade is a plain tier change with no
   * comp re-fire. (A free->paid upgrade ALWAYS fires activation regardless —
   * that slot is earning for the first time.) Some tenants want tier-up
   * bonuses, some don't, so it's configurable.
   */
  paid_upgrade_refires_bonuses: boolean;

  /**
   * When true (default), a slot's Stairstep rank is recomputed LIVE on slot
   * activation and product repurchase (the worker re-evaluates the slot's rank
   * from its current period PV right after the PV rollup) so the rank — and the
   * rank-based product pricing that follows it — updates immediately. When
   * false, rank only advances at period close (the legacy batch behavior).
   * Ranks are monotonic either way: a recompute can only RAISE a rank, never
   * lower it.
   */
  stairstep_live_rankup: boolean;

  /**
   * When true, reaching a new (higher) Stairstep rank pays a one-time bonus
   * from that rank's `reward_cash` / `reward_gc` (configured per rank in the
   * comp-plan editor). Paid once per (slot, rank) for the slot's lifetime.
   * Default false — most tenants don't run a rank-up bonus.
   */
  stairstep_rankup_bonus: boolean;

  /**
   * Deductions applied when a member requests a payout: withholding tax
   * (flat-rate-by-bracket), a service charge, and an admin fee — keyed per
   * payout method with a `default` fallback. The wallet is still debited the
   * GROSS amount; deductions determine the NET the member receives. All zero
   * (disabled) by default. See `payout-deductions.ts` for the math.
   */
  payout_deductions: PayoutDeductionsConfig;

  /**
   * When the cash-out window is open. `POST /payouts/requests` is rejected
   * outside it (and the member SPA disables the submit button + shows the next
   * open time). Evaluated in the tenant timezone (`tenant_config.timezone`).
   * `daysOfWeek` is 0=Sun..6=Sat; the window is `[openHour, closeHour)` in
   * 24h local time. Disabled by default (always open).
   */
  cashout_schedule: CashoutSchedule;

  /**
   * When true, a member must have `kyc_status = 'verified'` before
   * `POST /payouts/requests` is accepted (else `kyc_not_verified`). When false,
   * cash-out skips the KYC gate entirely. Defaults to TRUE to preserve the
   * historical hard requirement; admins toggle it from Tenant Settings.
   */
  cashout_require_kyc: boolean;

  /**
   * When true, `POST /payouts/requests` overwrites every payout-method field
   * tagged `role: 'account_holder'` with the account owner's `users.full_name`
   * (server-authoritative; the member can't type a different name). Guards the
   * "Person A accessed Person B's account and cashed out under A's name" case.
   * No-op if no method field is tagged `account_holder`. Default OFF (opt-in);
   * existing tenants keep the free-text name field.
   */
  lock_cashout_name_to_owner: boolean;

  /**
   * When true, a member must have a saved payout QR for the method they're
   * cashing out with before `POST /payouts/requests` is accepted (else
   * `payout_qr_required`). The QR is managed in the member's Settings → Payout
   * QR tab and is snapshotted onto the request at submit time. Default OFF
   * (opt-in) — the QR is a convenience for the disbursing clerk, not a
   * universal requirement, and turning it on blocks every member who hasn't
   * uploaded one yet.
   */
  payout_qr_required: boolean;

  /**
   * When true, a cash-out account number is UNIQUE across members: the first
   * member to cash out with a given (method, account number) claims it in the
   * `payout_accounts` registry, and any OTHER member submitting the same account
   * is rejected (`cashout_account_taken`). The same member may reuse their own
   * account across their slots. The account-number value is read from the field
   * tagged `role: 'account_number'` (fallback: legacy `account` key). Default OFF
   * (opt-in); existing tenants see no change.
   */
  unique_cashout_account_per_user: boolean;

  /**
   * When true, only members with `kyc_status = 'verified'` may pay with a
   * Cash-on-Delivery (COD/CODM) ecom payment method: the server omits COD from
   * `GET /storefront/payment-methods` for unverified members and `POST /orders`
   * rejects a COD order with `kyc_required_for_cod`. When false (default), COD
   * is unrestricted — this is opt-in, so existing tenants see no change.
   */
  cod_requires_kyc: boolean;

  /**
   * "Approve anyway" on proof of payment. Normally an admin must blind-type the
   * reference number off the proof image (`POST /payments/manual/:id/
   * verify-reference`) before Approve unlocks; without it the review throws
   * `reference_not_verified`. That gate has no escape hatch, so a legitimate
   * payment whose reference is illegible/cropped can never be approved.
   *
   * When true, a holder of `payment.approve` may approve WITHOUT verifying, by
   * passing `force: true` plus a mandatory free-text `forceReason`. The override
   * writes a second audit row (`manual_payment.force_approved`) carrying the
   * reason, mirroring `manual_payment.reference_mismatch_approved`.
   *
   * Deliberately NARROW — it overrides ONLY the unverified-reference gate. An
   * infected upload (`cannot_approve_infected_upload`) and a duplicate
   * admin-typed reference stay hard-blocked; neither is a legibility problem and
   * overriding them buys nothing. Default FALSE (opt-in).
   */
  manual_payment_force_approve: boolean;

  /**
   * Admin-defined KYC document requirements the member uploads for review (one
   * upload slot per entry). Empty falls back to the built-in starter set in the
   * member UI so KYC is never un-submittable. See {@link KycRequirementDef}.
   */
  kyc_requirements: KycRequirementDef[];

  /**
   * Min/max bounds on cash-out requests (per-slot single-request bounds +
   * per-member aggregate bounds). Enforced server-side; the member SPA reads
   * them to hint + pre-disable submit. All `null` (unbounded) by default.
   */
  cashout_limits: CashoutLimits;

  /**
   * When true (default), a member may have multiple outstanding cash-out
   * requests at once. When false, a new `POST /payouts/requests` is rejected
   * (`existing_pending_cashout_request`) while the member already has a request
   * in `requested`/`queued`/`processing` — the scope of "already has one" is
   * controlled by `cashout_multiple_scope`.
   */
  multiple_cashout_requests_allowed: boolean;

  /**
   * What counts as an existing pending request when
   * `multiple_cashout_requests_allowed` is false: `'member'` blocks if ANY of
   * the member's slots has a pending request; `'slot'` blocks only the same
   * slot (a different slot may still request). Defaults to `'member'`.
   */
  cashout_multiple_scope: 'member' | 'slot';

  /**
   * When true (default), a member may cancel their own not-yet-batched
   * (`requested`) cash-out request via `POST /payouts/requests/:id/cancel`.
   * When false, members cannot self-cancel (`cancellation_not_allowed`); only
   * an admin reject can reverse a request.
   */
  cashout_cancellation_allowed: boolean;

  /** Tenant constants stamped on every row of the payout disbursement file. */
  payout_export: PayoutExportConfig;

  /**
   * Admin-managed registry of ecom payment methods. Each entry pairs a
   * `currency` (cash | gc | coin) with a `settlement` ('wallet' for instant
   * slot-wallet debit, 'manual' for proof-of-payment upload + admin approval).
   * Members pick one at checkout; the route resolves wallet/manual via
   * `resolveEcomPaymentMethod`. Empty by default — admin populates it from
   * Tenant Settings. See `ecom-payment-methods.ts` for the shape.
   */
  ecom_payment_methods: EcomPaymentMethodsConfig;

  /**
   * Admin-managed registry of POS (Cashier) payment methods. Each entry pairs a
   * `tenderKind` (cash-with-change | exact non-cash | wallet debit) with an
   * optional wallet `currency`. The cashier reads enabled methods from
   * `GET /pos/payment-methods` and records the chosen method's code+label on
   * `pos_payments`. Defaults to the built-in Cash/Card/GC/Coin set (the former
   * hardcoded tenders) so POS works before any admin config. See
   * `pos-payment-methods.ts` for the shape.
   */
  pos_payment_methods: PosPaymentMethodsConfig;

  /**
   * Global kill-switch for online payment gateways (Dragonpay / iPay88). When
   * off (default), the `POST /payments/gateway/initiate` route 404s even if a
   * `settlement:'gateway'` method is enabled in the registry — so an admin can't
   * expose a gateway that can't actually transact (e.g. before env creds are set).
   */
  gateway_payments_enabled: boolean;

  /**
   * BF1: admin-authored Terms of Service shown on signup. `terms_version` is
   * bumped whenever the text changes; a member whose `terms_version_accepted`
   * is below the current version is re-prompted on next login.
   */
  terms_text: string;
  terms_version: number;

  /**
   * BF1: Privacy Policy, tracked separately from Terms so each can be revised
   * (and re-consented) independently.
   */
  privacy_policy_text: string;
  privacy_policy_version: number;

  /**
   * BF1: when true, signup requires accepting the current Terms version and a
   * version bump re-prompts existing members at login. Defaults to FALSE so a
   * fresh tenant (empty `terms_text`) doesn't block signups before the admin
   * has authored terms — flip it on once the text is published.
   */
  require_terms_on_signup: boolean;

  /**
   * When true, `/auth/signup` requires a unique `username` (in addition to
   * email). Defaults to FALSE so existing email-only signups keep working.
   * The member signup form shows the username field + a live availability
   * check only when this is on.
   */
  require_username_on_signup: boolean;

  /**
   * Admin-defined extra fields collected on member signup (text inputs or file
   * uploads). Empty by default; the signup form renders them in `order` and the
   * values land on `users.registration_data`. See {@link RegistrationField}.
   */
  registration_fields: RegistrationField[];

  /**
   * Which fields a member may edit on their Profile tab (name / phone / photo).
   * Enforced server-side by `PATCH /me`. See {@link MemberEditableFields}.
   */
  member_profile_editable: MemberEditableFields;

  /**
   * Which fields a merchant may edit on their Profile page (business name /
   * owner name / contact number / contact email / pickup address). Enforced
   * server-side by `PATCH /merchant/me`. See {@link MerchantEditableFields}.
   */
  merchant_profile_editable: MerchantEditableFields;

  /**
   * When true, `/auth/login` accepts a username OR an email in the identifier
   * field for member logins. Defaults to FALSE (email-only). Admin + cashier
   * logins are always email-only regardless of this flag. Independent of
   * `require_username_on_signup` so a tenant can collect usernames as identity
   * without enabling username login.
   */
  allow_login_by_username: boolean;

  /**
   * Activation terms shown at slot activation (kit redeem). Mirrors the BF1
   * Terms pattern: `activation_terms_version` is bumped when the text changes,
   * and the accepted version is recorded on the slot. The classic use is the
   * "activation cannot be changed once done" agreement.
   */
  activation_terms_text: string;
  activation_terms_version: number;

  /**
   * Admin-editable success message shown to the member right after a slot is
   * activated (kit redeem). Plain text; returned by `/kits/redeem` so the member
   * SPA can surface it in the activation success toast/modal. Default
   * "Congratulations! Your slot is now active."
   */
  activation_success_message: string;

  /**
   * When true, `/kits/redeem` requires the member to accept the current
   * `activation_terms_version` before a slot is activated. Defaults to FALSE so
   * a tenant with empty activation terms never blocks redeems.
   */
  require_activation_terms: boolean;

  /**
   * Storefront: congratulatory message shown in the "Order placed!" dialog after
   * a successful checkout. Admin-editable; exposed via the public
   * `GET /tenant/config` so the storefront can render it.
   */
  order_success_message: string;

  /**
   * Member portal: message shown in the dialog that greets a member on login when
   * they have one or more unpaid orders (status pending/to_pay). Admin-editable;
   * exposed via the public `GET /tenant/config`.
   */
  unpaid_orders_dialog_message: string;

  /**
   * Admin-configurable default sponsor for self-signups without a `?ref=` code.
   * The user_id (NOT a referral code) of an active member; that user becomes the
   * new member's `sponsor_user_id`. When `null` (default), signup falls back to
   * super_admin (legacy Phase 10 behavior). Setting this lets a tenant tag all
   * direct registrations under a marketing-leader account instead of root.
   *
   * LEGACY: superseded by `default_sponsor_slot_code` (a specific slot, not a
   * user). Kept as a fallback so already-configured tenants keep working — at
   * signup the slot_code is preferred, then this user, then super_admin.
   */
  default_sponsor_user_id: string | null;

  /**
   * Walk-in default sponsor SLOT for self-signups without a `?ref=` code — the
   * slot_code of an active slot. The new member registers directly under this
   * slot (both `sponsor_user_id` and `registered_under_slot_id` are set from
   * it). `null` (default) falls back to `default_sponsor_user_id`, then
   * super_admin. Preferred over the legacy user-level field because it removes
   * the "which of the member's slots?" ambiguity. Resolved + re-validated
   * active at use time via `resolveReferralSlot`.
   */
  default_sponsor_slot_code: string | null;

  /**
   * Global override for LINK registrations (signups that arrive with a `?ref=`
   * code). When set to an active slot_code, EVERY link signup is routed under
   * this slot regardless of which link was used. The original link is still
   * counted as used (`incrementReferralUse`) and the member is still
   * `referred_via_link`. `null` (default) = normal per-link behavior (register
   * under the slot the link resolves to).
   */
  link_sponsor_override_slot_code: string | null;

  /**
   * When true (default), a member's SECOND-and-later slots suggest the member's
   * OWN first-created slot as the sponsor (self-stacking) instead of the
   * original signup sponsor. This is only a pre-filled suggestion — the member
   * can still type a different sponsor at redeem. A member's FIRST slot always
   * uses the registration sponsor regardless of this flag.
   */
  extra_slots_self_sponsor: boolean;

  /**
   * Sponsor-editability at slot activation/redeem. Each governs whether the
   * member may OVERRIDE the pre-filled sponsor (by typing a `sponsorCode`) for a
   * distinct, non-overlapping activation domain. All default `true` (editable —
   * current behavior); flipping one to `false` LOCKS that domain's sponsor to the
   * computed default (an admin `sponsor_override_slot_id` still wins over all).
   *  - `sponsor_editable_from_replicated_link`: the member's FIRST slot when they
   *    registered via a replicated/referral link (`users.referred_via_link`).
   *    Locked default = `users.registered_under_slot_id`.
   *  - `sponsor_editable_extra_slots`: a SECOND-or-later slot while
   *    `extra_slots_self_sponsor` is OFF (default = signup sponsor's primary).
   *  - `sponsor_editable_self_stack`: a SECOND-or-later slot while
   *    `extra_slots_self_sponsor` is ON (self-stack; default = member's own slot).
   * A first-slot WALK-IN (no link) is governed by none of these — always editable.
   */
  sponsor_editable_from_replicated_link: boolean;
  sponsor_editable_extra_slots: boolean;
  sponsor_editable_self_stack: boolean;

  /**
   * When true, the member dashboard's "Your standing" card shows the member's
   * sponsor (the owner of their active slot's `sponsor_slot_id`). Default
   * `false` (hidden) — display-only, no behavior change.
   */
  show_sponsor_on_dashboard: boolean;

  /**
   * Which detail fields render on each box of the BINARY genealogy tree (member
   * + admin). See {@link BinaryGenealogyNodeFields}. Display-only.
   */
  binary_genealogy_node_fields: BinaryGenealogyNodeFields;

  /**
   * Platform/domain license expiration as an ISO date string ("YYYY-MM-DD"), set
   * by the super-admin. Drives the admin renewal-reminder popup, which appears
   * on login within 3 months of (or past) this date. null = unset (no reminder).
   * Notification-only — it does NOT lock the tenant out.
   */
  domain_expires_at: string | null;

  /**
   * When true (default), the slot-activation/redeem form shows the sponsor
   * field. When false, the sponsor field is HIDDEN on redeem for members who
   * have an active admin sponsor override (`users.sponsor_override_slot_id`) —
   * a "stealth" mode so an overridden member can't see they were overridden.
   * Members WITHOUT an override always see the field regardless of this flag.
   */
  show_sponsor_on_redeem: boolean;

  /**
   * Gates who may register. `'on'` (default) = open registration via the signup
   * page and links. `'links_only'` = registration is allowed ONLY when the user
   * arrived through a referral or product-referral link (a `?ref=` sponsor code);
   * the standalone signup page without a link is rejected. `'by_sponsor_only'` =
   * the standalone signup page requires the user to supply a sponsor code.
   * Enforced server-side in `/auth/signup`; the member signup page reads it from
   * the public `GET /tenant/config` to adapt its form.
   */
  signup_mode: 'on' | 'links_only' | 'by_sponsor_only';

  /**
   * INFORMATION-SYSTEMS-ONLY mode: the public site becomes a pure marketing
   * brochure — no membership, no users, no shop. Just the tenant's own landing
   * pages, with login / signup / cart / search / account gone from the chrome.
   *
   * The member SPA mounts a REDUCED route tree (the bespoke landing + its own
   * pages only), so `/login`, `/signup`, `/cart`, `/checkout` and the whole
   * member portal are unreachable rather than merely hidden. `/impersonate`
   * stays mounted: it needs an admin-issued single-use token, and without it a
   * super-admin has no way onto the box's member side at all.
   *
   * Server-side, `/auth/signup` rejects outright and `/auth/login` rejects
   * member-only users arriving from the MEMBER origin — admin / cashier /
   * merchant SPAs sit on their own origins and are untouched, and an admin may
   * still log in on the member origin as a second escape hatch.
   *
   * REQUIRES a bespoke landing (`hasBespokeLanding`, enforced in
   * `PATCH /admin/tenant/features`) — otherwise `/` would render nothing.
   * Default false: the storefront is the norm.
   */
  public_site_info_only: boolean;

  /**
   * Storefront home de-duplication: what the `productFeed` grid ("Just For You")
   * leaves out because the same product is already headlined higher up the page.
   *
   * Both default FALSE = the grid lists the whole visible catalog, which is the
   * behavior every tenant had before these existed. They are opt-in per tenant
   * on purpose: `products.featured` has only ever been an ORDER BY boost, so
   * clients have ticked it liberally (one live tenant has it on 25 of 28
   * products) and flipping its meaning globally would empty their home page.
   *
   * Neither affects Shop, search, category pages or a direct link — a hidden
   * product stays findable everywhere else. This is page composition, not
   * visibility.
   */
  product_feed_hide_featured_packages: boolean;
  product_feed_hide_featured_products: boolean;

  /**
   * When true, a visitor who arrived through an ENABLED campaign (replicated)
   * page satisfies the restrictive `signup_mode`s — the page IS the invitation.
   * They carry no `?ref=`, so they register under `default_sponsor_slot_code`
   * like any other walk-in. This is the ref-less `/c/<slug>` funnel a virtual
   * product opens; without it that funnel dead-ends on a `links_only` /
   * `by_sponsor_only` tenant. The slug is validated server-side against an
   * enabled `replicated_pages` row — never trusted from the client. Default
   * false: invitation-only is a deliberate tenant choice, so widening it is
   * opt-in. No effect when `signup_mode` is `'on'`.
   */
  campaign_page_signup_allowed: boolean;

  /**
   * When true, members may set/change their own referral code from Settings
   * (subject to `referral_code_max_changes`). Default false (opt-in). Custom
   * codes resolve to the member as sponsor at signup exactly like the
   * auto-generated code.
   */
  member_referral_code_self_set: boolean;

  /**
   * Max number of times a member may change their referral code (when
   * `member_referral_code_self_set` is on). `0` disables changes; admins can
   * reset a member's counter to grant more. Default 1.
   */
  referral_code_max_changes: number;

  /**
   * When true, visiting a member's replicated/referral link shows the
   * admin-built landing page (a stack of typed blocks) BEFORE signup, instead
   * of dropping straight onto the signup form. The referral code in the URL
   * still drives sponsor attribution (the page's CTA carries it to signup).
   * Default false — the link keeps its current straight-to-signup behavior.
   */
  replicated_page_enabled: boolean;

  /**
   * When true, clicking a VIRTUAL product in the online store opens the
   * replicated page assigned to it (`products.replicated_page_id`, picked in
   * the admin product editor) instead of the normal product page — so a
   * service/virtual offer sells through its campaign landing page rather than a
   * cart-first product page.
   *
   * Narrow by design: only virtual products with an ENABLED page assigned are
   * redirected. Anything else — the flag off, a physical product, no page
   * assigned, a disabled or deleted page — keeps `/product/:id`. The canonical
   * product URL is never redirected either, so the Dropshipping share link
   * (`/product/:id?ref=`) and search results still work. Default false.
   */
  virtual_products_open_replicated_page: boolean;

  /**
   * Master switch for the VIRTUAL PRODUCTS feature in the ADMIN UI. When off
   * (default), nothing about virtual products is offered anywhere in admin: the
   * Virtual Products page and its sidebar entry are gone, the product editor
   * shows no "Virtual product / service" flag, and the two virtual policy
   * toggles above are hidden. A tenant that only sells physical goods never
   * meets the concept.
   *
   * DELIBERATELY UI-ONLY — it does NOT change runtime behaviour. A product
   * already carrying `is_virtual` keeps skipping shipping, dimensions and
   * inventory exactly as before, because silently re-introducing a courier fee
   * (or an inventory requirement) on a live service because someone flipped a
   * display toggle would be a money bug, not a preference. Those rows also stay
   * visible in the ordinary Products list while the flag is off, so they can
   * still be edited — they're just no longer split into their own catalog.
   *
   * Flip it on and everything appears; flip it off and it's hidden again. No
   * data is written or migrated either way.
   */
  virtual_products_enabled: boolean;

  /**
   * Does a slot's OWN personal PV (PPV) count toward its OWN group PV (GPV)?
   * Default `true` — GPV = own PPV + all descendants' PPV (the historical
   * behavior). When `false`, a slot's GPV reflects only its downline's volume;
   * the buyer's own purchase bumps PPV only, never its own GPV. Affects every
   * GPV-based gate (e.g. Stairstep `min_gpv`). Ancestors are unaffected.
   */
  gpv_includes_ppv: boolean;

  /**
   * F6 — Product Code Generation. When `true`, a PV-bearing purchase does NOT
   * roll up PV (PPV/GPV) at completion; instead the worker mints a PV-carrying
   * "product code" into the buyer's Code Vault. The buyer redeems it to release
   * the deferred PV (credited to the slot that bought — captured at purchase).
   * COMMISSIONS are unaffected (they still pay at purchase); only PV — and the
   * PV-driven live rank-up — is deferred. Kit orders are exempt (they mint their
   * own activation codes). Default `false` (PV rolls up immediately as before).
   */
  product_code_generation: boolean;

  /**
   * Stairstep payout-base policy. When `true`, a slot's OWN personal PV is
   * EXCLUDED from its Stairstep commissionable base — only downline (group)
   * legs' PPV drive the differential. Default `false` (the slot also earns on
   * its own PPV, the historical behavior + reference-impl parity). Rank
   * QUALIFICATION is unaffected — this only changes the payout base.
   */
  stairstep_exclude_personal_pv: boolean;

  /**
   * What per-month PV history members see on their dashboard. `'off'` hides the
   * card, `'ppv_only'` shows monthly personal PV, `'ppv_and_gpv'` (default) adds
   * group PV. Read-only display policy — does not affect any computation.
   */
  member_pv_display: 'off' | 'ppv_only' | 'ppv_and_gpv';

  /**
   * Dropshipping Income: master switch for the per-product replicated-link
   * retail-margin earning. When on, members can share a product link
   * (`/product/{id}?ref={code}`); an outside buyer who registers and buys
   * through it pays full SRP, becomes a lead of the sharer, and the sharer earns
   * `(SRP − sharer's own discounted price) × qty` per discounted line via the
   * `dropshipping_income` comp plan. Default false (opt-in).
   */
  dropshipping_income: boolean;

  /**
   * Pay the inviter on a "register, then pay" funnel sale. When on, a visitor
   * who reaches `/buy/:productId` through someone's campaign link checks out as
   * a DROPSHIP sale attributed to that sharer's slot, so the inviter earns the
   * retail margin on a product the buyer bought before ever activating.
   *
   * Without it a funnel sale pays nobody: the buyer holds no slot, so
   * `orders.buyer_slot_id` is null and the comp gate skips the order entirely.
   * `users.sponsor_user_id` records who introduced them but carries no money.
   *
   * REQUIRES `dropshipping_income` — this reuses that mechanism rather than
   * inventing a second one, and inherits its semantics wholesale:
   *  - the buyer pays SRP (that margin IS the inviter's income), and
   *  - the order settles by manual proof, never a wallet or gateway.
   * With Dropshipping Income off, the funnel still works; the sale is simply
   * unattributed, exactly as it is today.
   *
   * Attribution is session-scoped, like every other dropship link: a lead who
   * comes back later without the ref places an ordinary, unattributed order.
   * Default false.
   */
  virtual_funnel_dropship_attribution: boolean;

  /**
   * Replicated-link PV to owner: when on, a non-activated buyer's SRP purchase
   * through a replicated link credits the link OWNER's PV as if the owner bought
   * it (owner PPV + owner's upline GPV rollup); the buyer's own PV is untouched.
   * Independent of `dropshipping_income` (the cash retail-margin switch) — either
   * may be on alone. Default false (opt-in).
   */
  replicated_link_pv_to_owner: boolean;

  /**
   * When on, the shareable replicated link emits an OPAQUE encrypted ref token
   * (`?ref=e1.<base64url>`) instead of the plaintext referral code. Resolution
   * is always backward-compatible — both forms are accepted regardless of this
   * flag — so flipping it never breaks links already shared. Default false (the
   * link keeps exposing the human-readable code).
   */
  replicated_link_ref_encrypted: boolean;

  /**
   * BF4: when true, orders paid in a NON-cash currency (gc / coin) fire the
   * `order_completed` comp event like a cash sale — uplines earn commissions
   * and the buyer's product `coin_grant` is granted. Defaults to FALSE: a
   * non-cash order fires NO comp at all (no commissions, no points/tokens, no
   * coin-grant — avoids a coin-farming loop). Cash orders are never affected.
   */
  commissions_on_non_cash: boolean;

  /**
   * BF4: when true, an ecom order settled from a WALLET (gc / coin / cash-wallet)
   * that has a SHIP-TO ADDRESS quotes shipping normally, folds the peso courier
   * fee into the order's own currency 1:1 (one combined `checkout-<orderId>`
   * wallet debit covers goods + shipping), and walks the real fulfilment ladder
   * (paid → to_ship → shipping → delivered → completed) instead of
   * instant-completing. Pickup / address-less wallet orders are UNCHANGED:
   * instant complete, zero shipping. Default FALSE — with it off a wallet order
   * can't carry shipping (`shipping_not_allowed_for_non_cash`) and completes at
   * checkout exactly as before. NOTE: with a ladder GC order, kit codes and
   * commissions follow `kit_code_reveal_on` / `ecom_event_on` rather than firing
   * at checkout, and a gc/coin order still pays uplines only when
   * `commissions_on_non_cash` is also on.
   */
  wallet_orders_charge_shipping: boolean;

  /**
   * Shows the Accounting section in the admin sidebar (/accounting) — preview
   * UI until the accounting module ships. Flipped from Settings → System
   * (super-admin only, like every non-storefront feature key).
   */
  accounting_enabled: boolean;

  /**
   * Membership tier whose per-product ABSOLUTE prices apply to a buyer with NO
   * active slot ("non-activated member"), so a prospect can enjoy a member
   * price before they activate. `null` (default) = slotless buyers pay SRP,
   * which is the historical behaviour.
   *
   * The predicate is deliberately "the buyer has no ACTIVE slot at all", NOT
   * "no slot was supplied for pricing" — a cashier who deselects an activated
   * member's slot at the till must not unlock this discount. Rank pricing never
   * applies (no slot ⇒ no rank) and PV is never affected. Dropship orders are
   * exempt (the buyer pays SRP by design so the sharer's margin holds).
   *
   * Validated against `memberships.id` when saved; a tier archived afterwards
   * simply stops matching and the buyer falls back to SRP.
   */
  non_activated_pricing_membership_id: string | null;

  /**
   * Which slot's per-product ABSOLUTE price a member with MULTIPLE slots pays.
   *
   * - `'active_slot'` (default) — the tier/rank of the slot they are currently
   *   logged in on. Switching slots re-prices the catalog.
   * - `'best_across_slots'` — the lowest price any of their ACTIVE slots
   *   qualifies for, regardless of which one is selected.
   *
   * PRICING ONLY. `orders.buyer_slot_id` — and therefore PV, commissions,
   * binary points, the CD garnish and the wallet debit — always follows the
   * SELECTED slot in both modes. Pricing slot and attribution slot are separate
   * concepts; see `resolveBuyerSegmentKeys`.
   *
   * Stockist / branch prices are NEVER unioned across slots: `branch_pricing`
   * is a BRANCH entitlement, so unioning would hand a member stockist pricing
   * at the till on an ordinary retail sale merely because they own a stockist
   * branch on some other slot. Non-activated ("prospect") pricing is likewise
   * unreachable in best mode — it requires zero active slots by definition.
   */
  pricing_slot_source: PricingSlotSource;

  /**
   * BF4: display name for the coin currency (e.g. "Gems", "Reward Points").
   * Surfaced everywhere coins appear in the SPAs. Default "Coins".
   */
  coin_label: string;

  /**
   * BF4: per-plan coin reward, keyed by `plan_code` (parallel to `daily_caps`).
   * When a plan pays a CASH credit, the framework rides a flat coin credit of
   * `coin_rewards[plan_code]` onto each earner slot (`currency_kind='coin'`,
   * kind `coin_reward`) — "coins alongside money" for any plan, no per-plan
   * code. Missing key / `<= 0` = that plan pays no coins. Free slots are
   * filtered out as earners (BF2), so they receive no coin reward either.
   */
  coin_rewards: Record<string, number>;

  /**
   * Admin-friendly display names for ledger `kind` values, keyed by the raw
   * kind (e.g. `{ direct: 'Direct Referral' }`). Sparse — only the entries an
   * admin renamed. Built-in defaults for every known kind live in
   * `LEDGER_KIND_LABEL_DEFAULTS` (`ledger-labels.ts`); `labelizeLedgerKind`
   * resolves override → default → humanized fallback. Shown in member Earnings
   * + admin wallet/ledger views.
   */
  ledger_labels: Record<string, string>;

  /**
   * Admin-friendly display names for ledger `source_plan` values, same shape +
   * resolution as `ledger_labels` (defaults in `LEDGER_SOURCE_PLAN_LABEL_DEFAULTS`).
   */
  ledger_source_plan_labels: Record<string, string>;

  /**
   * JM-EX9: admin-authored member notification templates. The materializer
   * uses these when fanning out an in-app notification per realized ledger
   * credit. Today only `earnings` is wired; future kinds (signup, payout)
   * extend this map.
   *
   * Placeholders supported in title/body (Mustache-ish `{name}`):
   *   {amount}        — formatted credit amount
   *   {currency}      — Cash / GC / Coin / Token
   *   {source_plan}   — humanized via labelizeSourcePlan
   *   {kind}          — humanized via labelizeLedgerKind
   *   {trigger_user}  — buyer/redeemer display name when resolvable, else "—"
   */
  notification_templates: NotificationTemplatesConfig;

  /**
   * Merchant feature (M1): default platform fee % charged on every merchant
   * product sale. Applied when a merchant adds a product (admin can override
   * per product at approval) and SNAPSHOTTED onto `products.platform_fee_pct`,
   * so changing this never rewrites past sales. The merchant earns
   * `lineTotal × (1 − pct/100)`; the platform keeps the rest. Money string.
   */
  platform_fee_pct: string;

  /**
   * Merchant feature: the inclusive `[min, max]` band a MERCHANT-declared
   * platform fee % must fall in. The merchant sets their own fee per product;
   * the server clamps it to this range (`clampFeeToRange`) on submit and admin
   * approval. `platform_fee_pct` (the default) should sit inside the band.
   * Money strings (0–100).
   */
  platform_fee_min_pct: string;
  platform_fee_max_pct: string;

  /**
   * Merchant pricing model: default PV-commission % the admin sees pre-filled at
   * product review. PV for a merchant product = `pv_commission_pct% × commission`
   * (commission = price × platform_fee_pct%). Per-product overridable. Money string.
   */
  merchant_pv_commission_pct: string;

  /**
   * Merchant pricing model: default VAT % applied to the merchant's net credit
   * when VAT is enabled on a product (`net = gross × (1 − vat_pct/100)`). Money string.
   */
  merchant_default_vat_pct: string;

  /**
   * Realtime Unilevel (RTU) defaults pre-filled in the merchant-product approval
   * dialog's RTU tab. The pool = `merchant_rtu_ceiling_pct% × commission`, split
   * across sponsor levels by `merchant_rtu_default_shares` — each entry is that
   * level's % OF THE POOL (index 0 = buyer L0). Level count = array length. The
   * admin can override per product; the resolved per-level shares are stored on
   * the product. Money strings (each 0–100).
   */
  merchant_rtu_ceiling_pct: string;
  merchant_rtu_default_shares: string[];

  /**
   * Merchant pricing model: default state of the per-product VAT toggle the
   * admin sees at product review. Per-product overridable.
   */
  merchant_vat_enabled_default: boolean;

  /**
   * Merchant feature (M1): when true, a completed merchant sub-order ALSO fires
   * the `order_completed` comp event (PV/points/commissions to uplines) like a
   * house order. Default FALSE = marketplace-only: merchant sales carry no PV
   * and fire no comp; they only credit the merchant wallet.
   */
  merchant_sales_earn_comp: boolean;

  /**
   * Order-by-Area: when true, an ecom "ship" order's fulfilling branch
   * (`ship_from_branch_id`) is AUTO-ROUTED from the buyer's shipping region to
   * the ecommerce-ready branch/stockist tagged with that courier zone (falling
   * back to the designated Main branch), replacing the member's manual branch
   * pick. Default FALSE = legacy behavior (member picks / admin assigns).
   */
  order_by_area: boolean;

  /**
   * Order-by-Area: when true, a stockist that FULFILLS a house ecom order
   * accrues a receivable (Main owes the stockist the full retail sale value) on
   * the stockist ledger, reversed on refund/cancel. Default FALSE.
   */
  stockist_receivables: boolean;

  /**
   * Merchant feature (M1): admin-defined registration requirements shown on the
   * public merchant registration form (file uploads / text fields). Empty by
   * default — the admin configures them in Settings. See `merchant.ts`.
   */
  merchant_requirements: MerchantRequirementDef[];

  /**
   * Merchant feature (M1): payout method registry + deductions for MERCHANT
   * cash-outs, independent of the member `payout_deductions` registry (merchants
   * use different methods/schedules). Same shape + math.
   */
  merchant_payout_deductions: PayoutDeductionsConfig;

  /**
   * Merchant feature (M1): cadence the admin batches merchant payouts on
   * (informational; the batch flow is manual). Default monthly.
   */
  merchant_payout_schedule: MerchantPayoutSchedule;

  /**
   * Storefront merchandising: the home "Flash Sale" strip. `productIds` are the
   * products to feature, `endsAt` an optional ISO countdown target (null = no
   * countdown), `enabled` the on/off. The public storefront reads the resolved
   * product rows via `GET /storefront/flash-sale`; when disabled or empty the
   * strip is hidden. Admin-managed in the Flash Sale tab.
   */
  flash_sale: FlashSaleConfig;

  /**
   * Storefront landing "packages" — an admin-curated, ORDERED list of product ids
   * surfaced in a tenant landing's packages section (e.g. mhyel's bespoke landing).
   * Resolved to live product rows via `GET /storefront/landing-packages` so the
   * cards are real, cart-able products. Empty = the section is hidden / falls back.
   * Admin-managed in the Landing Packages tab.
   */
  landing_packages: LandingPackagesConfig;

  /**
   * Storefront "Featured Packages" — an admin-curated, ORDERED list of product
   * ids headlined in a placeable `featuredPackages` storefront section. Resolved
   * to live, cart-able product rows via `GET /storefront/featured-packages` so
   * the cards are real products. `enabled` is the on/off, `heading` an optional
   * section-title override. Empty or disabled = the section is hidden.
   * Admin-managed in the Featured Packages tab.
   */
  featured_packages: FeaturedPackagesConfig;

  /**
   * Storefront CMS content: hero slides, promo/campaign banners, vouchers,
   * collections, store-stat blocks, trust/assurance badges, header links +
   * search suggestions, footer columns + brand copy, and a few inline copy
   * strings. Admin-managed in the Storefront section; served to the public
   * buyer storefront via `GET /storefront/content`. See `storefront-content.ts`.
   */
  storefront: StorefrontConfig;

  /**
   * Chat (M-chat): master on/off for buyer↔seller messaging. When false
   * (default, opt-in) the "Message seller" button + chat bells are hidden and
   * the `/me/conversations`, `/admin/conversations`, `/merchant/conversations`
   * routes 403 — so a tenant that doesn't use chat carries zero polling load.
   */
  messaging_enabled: boolean;

  /**
   * Auto chat-block on spam. When ON, a member who sends at least
   * `chat_spam_threshold` messages within the trailing `chat_spam_window_seconds`
   * is automatically chat-blocked (`users.chat_blocked = true`, audited with
   * `reason: 'auto_spam'`) — admin/merchant replies are unaffected and an admin
   * can unblock from the Messages inbox. OFF by default (opt-in); the defaults
   * sit ABOVE the per-minute send rate-limit so only sustained flooding trips it.
   */
  chat_spam_autoblock_enabled: boolean;
  /** Trailing window (seconds) the auto-block counts a member's messages over. */
  chat_spam_window_seconds: number;
  /** Member message count within the window that trips the auto chat-block. */
  chat_spam_threshold: number;

  /**
   * Auto-Ship: master on/off for the member monthly auto-ship subscriptions.
   * ON by default (the feature shipped always-enabled). When false the member
   * Auto-Ship nav item is hidden, `POST/PATCH /me/autoship` 403, and the worker
   * engine (`processDueAutoships`) skips all due subscriptions — existing rows
   * are preserved and resume when re-enabled.
   */
  autoship_enabled: boolean;

  /**
   * Member monthly subscription (Phase C): master switch for the paid
   * subscription that unlocks the admin-configured "special links" (Zoom/Vimeo
   * live). Off = the subscribe page + special-links surface are hidden and the
   * wallet renewal engine is a no-op (existing access periods still run out).
   * Default off.
   */
  member_subscription_enabled: boolean;
  /**
   * Member subscription plan config: the monthly `price` (money string) and the
   * wallet `currency` charged ('cash' | 'gc'). Read by the subscribe page + the
   * wallet renewal engine.
   */
  member_subscription: { price: string; currency: 'cash' | 'gc' };

  /**
   * AI Assistant (Phase 1): master switch for the member-facing /assistant
   * page + API. Off = nav item hidden, assistant routes 403, and the member
   * page (if deep-linked) shows only the scripted help fallback. Default OFF.
   * Enabling also requires the server-side ANTHROPIC_API_KEY — without it the
   * API degrades with `assistant_not_configured` and the UI falls back.
   */
  assistant_enabled: boolean;
  /**
   * AI Assistant config (operator-private — never exposed through
   * `GET /tenant/config`; only the boolean flag above is public). See
   * `AssistantConfig` for per-key docs.
   */
  assistant: AssistantConfig;
};

/**
 * Claude models a tenant may select for the assistant. Haiku is the cost
 * default; Sonnet/Opus are per-tenant upgrades (roughly 3× / 5× input cost).
 * The API validates against this allowlist and silently falls back to the
 * default on an unknown id.
 */
export const ASSISTANT_MODELS = ['claude-haiku-4-5', 'claude-sonnet-5', 'claude-opus-5'] as const;
export type AssistantModel = (typeof ASSISTANT_MODELS)[number];

export type AssistantConfig = {
  /** Claude model id from `ASSISTANT_MODELS`. */
  model: AssistantModel;
  /** Assistant messages per member per tenant-day; `<= 0` = unlimited. */
  daily_cap: number;
  /**
   * Monthly spend ceiling in USD (money string) across the whole tenant,
   * compared against the summed per-exchange cost estimates. `''`/`'0'` =
   * unlimited. When exhausted, members get the scripted fallback until the
   * next month.
   */
  monthly_budget_usd: string;
  /** Allow the server-side web-search tool (~$0.01 per search, budget-capped). */
  web_search_enabled: boolean;
  /**
   * Cross-chat recall: let the assistant carry context between a member's own
   * conversations. Turns on BOTH layers — the background digest of idle threads
   * (folded into later chats' seed) and the `search_past_chats` /
   * `read_past_chat` tools.
   *
   * Off by default: it adds a small background spend per idle thread and is a
   * privacy posture a tenant should choose rather than inherit. A member can
   * still opt out individually (`users.assistant_recall_opt_out`) — recall
   * requires BOTH.
   */
  recall_enabled: boolean;
  /**
   * How long a thread must sit untouched before it's eligible for a digest.
   * Long enough that a member on a lunch break doesn't get their live thread
   * summarised mid-conversation.
   */
  recall_idle_minutes: number;
  /**
   * Only digest threads whose last message is newer than this. This is the
   * anti-stampede guard: without it, the first sweep after `recall_enabled`
   * flips on would make EVERY historical thread eligible at once and burn the
   * month's budget in a single tick.
   */
  recall_digest_max_age_days: number;
  /**
   * Persona overrides merged over `DEFAULT_PERSONAS` by id (see
   * `assistant/personas.ts`). Editor UI is Phase 2 — Phase 1 admin shows the
   * resolved set read-only.
   */
  personas: AssistantPersonaOverride[];
  /**
   * Tenant-authored knowledge text folded into the assistant's system prompt
   * alongside the storefront FAQ (company story, product notes, policies…).
   */
  knowledge: string;
};

export const ASSISTANT_CONFIG_DEFAULT: AssistantConfig = {
  model: 'claude-haiku-4-5',
  daily_cap: 30,
  monthly_budget_usd: '',
  web_search_enabled: true,
  recall_enabled: false,
  recall_idle_minutes: 120,
  recall_digest_max_age_days: 30,
  personas: [],
  knowledge: '',
};

export type FlashSaleConfig = {
  enabled: boolean;
  productIds: string[];
  endsAt: string | null;
};

export type LandingPackagesConfig = {
  /** Ordered product ids; the landing maps them to its package cards in order. */
  productIds: string[];
};

export type FeaturedPackagesConfig = {
  /** On/off for the Featured Packages storefront section. */
  enabled: boolean;
  /** Ordered product ids; the section renders them as cards in this order. */
  productIds: string[];
  /** Optional section-heading override (null = the section's built-in default). */
  heading: string | null;
};

export type NotificationEventTemplate = {
  enabled: boolean;
  title: string;
  body: string;
};

export type NotificationTemplatesConfig = {
  earnings: NotificationEventTemplate;
  /**
   * Per-event member notification copy, keyed by a stable event code (see
   * `NOTIFICATION_EVENT_CODES`). The API renders these when it fires the
   * matching event (order placed, payment submitted, cash-out, …). Placeholders:
   *   {order_code} {amount} {currency} {method} {reason} {product}
   * Disabled events fire no notification. Optional on the wire (a partial PATCH
   * may omit it); `resolveFeatures` always fills it from defaults.
   */
  events?: Record<string, NotificationEventTemplate>;
};

/** The member-facing notification events with admin-editable copy. */
export const NOTIFICATION_EVENT_CODES = [
  'order_placed',
  'order_pending_payment',
  'payment_submitted',
  'dropship_sale',
  'cashout_requested',
  'cashout_rejected',
  'cashout_paid',
  'topup_submitted',
  'topup_approved',
  'topup_rejected',
  'autoship_completed',
  'autoship_skipped',
] as const;

export type NotificationEventCode = (typeof NOTIFICATION_EVENT_CODES)[number];

export const NOTIFICATION_TEMPLATES_DEFAULT: NotificationTemplatesConfig = {
  earnings: {
    enabled: true,
    title: 'You earned {amount} {currency}',
    body: 'From {source_plan} triggered by {trigger_user}',
  },
  events: {
    order_placed: {
      enabled: true,
      title: 'Order placed',
      body: 'We received your order {order_code}.',
    },
    order_pending_payment: {
      enabled: true,
      title: 'Payment needed for {order_code}',
      body: 'Upload your payment proof to start processing your order.',
    },
    payment_submitted: {
      enabled: true,
      title: 'Payment proof received',
      body: "We're reviewing your payment for order {order_code}.",
    },
    dropship_sale: {
      enabled: true,
      title: 'You made a sale!',
      body: 'Someone purchased {product} through your link — order {order_code}.',
    },
    cashout_requested: {
      enabled: true,
      title: 'Cash-out requested',
      body: 'Your cash-out of {amount} {currency} is pending review.',
    },
    cashout_rejected: {
      enabled: true,
      title: 'Cash-out rejected',
      body: 'Your cash-out was rejected: {reason}. The funds were returned to your wallet.',
    },
    cashout_paid: {
      enabled: true,
      title: 'Cash-out paid',
      body: 'Your cash-out of {amount} {currency} has been paid.',
    },
    topup_submitted: {
      enabled: true,
      title: 'Top-up proof received',
      body: "We're reviewing your wallet top-up of {amount} {currency}.",
    },
    topup_approved: {
      enabled: true,
      title: 'Wallet top-up approved',
      body: 'Your wallet was credited {amount} {currency}.',
    },
    topup_rejected: {
      enabled: true,
      title: 'Wallet top-up rejected',
      body: 'Your top-up was rejected: {reason}.',
    },
    autoship_completed: {
      enabled: true,
      title: 'Auto-ship order placed',
      body: 'Your auto-ship order {order_code} was placed and paid from your wallet.',
    },
    autoship_skipped: {
      enabled: true,
      title: 'Auto-ship skipped',
      body: "We couldn't run your auto-ship — your {currency} wallet was short. Top up and we'll try again next cycle.",
    },
  },
};

export const TENANT_FEATURE_DEFAULTS: TenantFeatures = {
  kit_codes_transferable: true,
  kit_codes_expire_days: null,
  kit_codes_downline_transfer: false,
  member_2fa_required: false,
  ecom_event_on: 'payment_approved',
  kit_code_reveal_on: 'completed',
  order_auto_complete_days: 7,
  virtual_orders_auto_complete: true,
  // Off by default: splitting a cart changes how many orders a buyer sees for
  // one payment, so no live tenant gets it without asking.
  virtual_orders_split_checkout: false,
  disabled_plans: [],
  earnings_breakdown_order: [],
  earnings_breakdown_rows: [],
  wallet_topup_enabled: false,
  wallet_topup_brackets: [],
  distribution_run_only_plans: [],
  company_auto_maintained_plans: ['stairstep', 'unilevel'],
  daily_caps: {},
  auto_place_slots: false,
  manual_placement_on_redeem: false,
  genealogy_activate_slot: true,
  free_slots_earn: true,
  package_replicated_link_enabled: true,
  total_saved_counter_enabled: false,
  cd_orders_pay_upline: true,
  cd_stairstep_count_immediately: true,
  half_cd_stairstep_count_immediately: true,
  cd_binary_mentor_count_directs: true,
  cd_binary_placement_count_directs: true,
  half_cd_triggers_like_ps: false,
  half_cd_debt_plans: [],
  cd_block_payout_in_debt: true,
  cd_topup_settles_debt: true,
  cd_topup_bonus_treatment: 'principal_only',
  max_slots_per_member: null,
  slot_code_series: { enabled: false },
  paid_upgrade_refires_bonuses: false,
  stairstep_live_rankup: true,
  stairstep_rankup_bonus: false,
  payout_deductions: PAYOUT_DEDUCTIONS_DEFAULT,
  cashout_schedule: CASHOUT_SCHEDULE_DEFAULT,
  cashout_require_kyc: true,
  lock_cashout_name_to_owner: false,
  payout_qr_required: false,
  unique_cashout_account_per_user: false,
  cod_requires_kyc: false,
  manual_payment_force_approve: false,
  kyc_requirements: KYC_REQUIREMENTS_DEFAULT,
  cashout_limits: CASHOUT_LIMITS_DEFAULT,
  multiple_cashout_requests_allowed: true,
  cashout_multiple_scope: 'member',
  cashout_cancellation_allowed: true,
  payout_export: PAYOUT_EXPORT_DEFAULT,
  ecom_payment_methods: ECOM_PAYMENT_METHODS_DEFAULT,
  pos_payment_methods: POS_PAYMENT_METHODS_DEFAULT,
  kit_auto_create_slot: { ecom: false, cashier: false },
  gateway_payments_enabled: false,
  terms_text: '',
  terms_version: 1,
  privacy_policy_text: '',
  privacy_policy_version: 1,
  require_terms_on_signup: false,
  require_username_on_signup: false,
  registration_fields: [],
  member_profile_editable: MEMBER_EDITABLE_FIELDS_DEFAULT,
  merchant_profile_editable: MERCHANT_EDITABLE_FIELDS_DEFAULT,
  allow_login_by_username: false,
  activation_terms_text: '',
  activation_terms_version: 1,
  activation_success_message: 'Congratulations! Your slot is now active.',
  require_activation_terms: false,
  order_success_message:
    "Thank you for your order! We've received it and will process it shortly.",
  unpaid_orders_dialog_message:
    'You have unpaid order/s, would you like to proceed with the payment steps?',
  default_sponsor_user_id: null,
  default_sponsor_slot_code: null,
  link_sponsor_override_slot_code: null,
  extra_slots_self_sponsor: true,
  sponsor_editable_from_replicated_link: true,
  sponsor_editable_extra_slots: true,
  sponsor_editable_self_stack: true,
  show_sponsor_on_dashboard: false,
  binary_genealogy_node_fields: BINARY_GENEALOGY_NODE_FIELDS_DEFAULT,
  domain_expires_at: null,
  show_sponsor_on_redeem: true,
  signup_mode: 'on',
  public_site_info_only: false,
  product_feed_hide_featured_packages: false,
  product_feed_hide_featured_products: false,
  campaign_page_signup_allowed: false,
  member_referral_code_self_set: false,
  referral_code_max_changes: 1,
  gpv_includes_ppv: true,
  product_code_generation: false,
  stairstep_exclude_personal_pv: false,
  member_pv_display: 'ppv_and_gpv',
  dropshipping_income: false,
  virtual_funnel_dropship_attribution: false,
  replicated_link_pv_to_owner: false,
  replicated_link_ref_encrypted: false,
  replicated_page_enabled: false,
  virtual_products_open_replicated_page: false,
  // Off by default: an existing tenant's admin is unchanged by the upgrade, and
  // a tenant that sells only physical goods never sees the concept at all.
  virtual_products_enabled: false,
  commissions_on_non_cash: false,
  wallet_orders_charge_shipping: false,
  accounting_enabled: false,
  non_activated_pricing_membership_id: null,
  // Default preserves today's behaviour: price follows the SELECTED slot.
  pricing_slot_source: 'active_slot',
  coin_label: 'Coins',
  coin_rewards: {},
  ledger_labels: {},
  ledger_source_plan_labels: {},
  notification_templates: NOTIFICATION_TEMPLATES_DEFAULT,
  platform_fee_pct: '10',
  platform_fee_min_pct: '0',
  platform_fee_max_pct: '100',
  merchant_pv_commission_pct: '50',
  merchant_default_vat_pct: '12',
  merchant_rtu_ceiling_pct: '20',
  merchant_rtu_default_shares: ['50', '30', '10', '5', '5'],
  merchant_vat_enabled_default: false,
  merchant_sales_earn_comp: false,
  order_by_area: false,
  stockist_receivables: false,
  merchant_requirements: MERCHANT_REQUIREMENTS_DEFAULT,
  merchant_payout_deductions: PAYOUT_DEDUCTIONS_DEFAULT,
  merchant_payout_schedule: 'monthly',
  flash_sale: { enabled: false, productIds: [], endsAt: null },
  landing_packages: { productIds: [] },
  featured_packages: { enabled: false, productIds: [], heading: null },
  storefront: STOREFRONT_DEFAULT,
  messaging_enabled: false,
  chat_spam_autoblock_enabled: false,
  chat_spam_window_seconds: 300,
  chat_spam_threshold: 60,
  autoship_enabled: true,
  member_subscription_enabled: false,
  member_subscription: { price: '0', currency: 'cash' },
  assistant_enabled: false,
  assistant: ASSISTANT_CONFIG_DEFAULT,
};

/**
 * Merge a partial features blob (from DB jsonb) with defaults — guarantees
 * every key is present and correctly typed. Unknown keys in `raw` are
 * preserved (forward compat: older code reading a newer DB doesn't drop
 * data).
 */
export function resolveFeatures(raw: Record<string, unknown> | null | undefined): TenantFeatures {
  const out = { ...TENANT_FEATURE_DEFAULTS } as TenantFeatures & Record<string, unknown>;
  if (!raw) return out;
  for (const key of Object.keys(raw)) {
    const v = raw[key];
    if (v === undefined) continue;
    (out as Record<string, unknown>)[key] = v;
  }
  // `storefront` is a nested config; deep-merge it against defaults so a blob
  // missing newer sub-blocks (forward-compat) still resolves to a complete,
  // correctly-typed config rather than dropping keys the shallow loop above
  // would leave partial.
  out.storefront = resolveStorefront(out.storefront as Partial<StorefrontConfig>);
  // Deep-merge notification templates so a blob missing newer event codes still
  // resolves every event to a default (forward-compat), and an admin who edited
  // one event keeps the rest defaulted.
  const nt = out.notification_templates as Partial<NotificationTemplatesConfig> | undefined;
  out.notification_templates = {
    earnings: nt?.earnings ?? NOTIFICATION_TEMPLATES_DEFAULT.earnings,
    events: { ...NOTIFICATION_TEMPLATES_DEFAULT.events, ...(nt?.events ?? {}) },
  };
  // Deep-merge cashout_limits so a blob missing a newer bound key still resolves
  // every bound (forward-compat), defaulting absent bounds to null (unbounded).
  out.cashout_limits = {
    ...CASHOUT_LIMITS_DEFAULT,
    ...(out.cashout_limits as Partial<CashoutLimits> | undefined),
  };
  // Same forward-compat reason: a tenant saved before a column existed must
  // still resolve every key, else the export emits `undefined` into a cell.
  out.payout_export = {
    ...PAYOUT_EXPORT_DEFAULT,
    ...(out.payout_export as Partial<PayoutExportConfig> | undefined),
  };
  // Deep-merge editable-field configs so a blob missing a newer field key still
  // resolves every flag (forward-compat), defaulting absent flags to editable.
  out.member_profile_editable = {
    ...MEMBER_EDITABLE_FIELDS_DEFAULT,
    ...(out.member_profile_editable as Partial<MemberEditableFields> | undefined),
  };
  out.binary_genealogy_node_fields = {
    ...BINARY_GENEALOGY_NODE_FIELDS_DEFAULT,
    ...(out.binary_genealogy_node_fields as Partial<BinaryGenealogyNodeFields> | undefined),
  };
  out.merchant_profile_editable = {
    ...MERCHANT_EDITABLE_FIELDS_DEFAULT,
    ...(out.merchant_profile_editable as Partial<MerchantEditableFields> | undefined),
  };
  // F5: deep-merge the per-channel auto-create flag so a blob with only one
  // channel set still resolves the other to its default (forward-compat).
  out.kit_auto_create_slot = {
    ecom: false,
    cashier: false,
    ...(out.kit_auto_create_slot as Partial<{ ecom: boolean; cashier: boolean }> | undefined),
  };
  // Deep-merge the subscription plan config so a blob with only `price` set still
  // resolves `currency` to its default (forward-compat).
  out.member_subscription = {
    price: '0',
    currency: 'cash',
    ...(out.member_subscription as Partial<{ price: string; currency: 'cash' | 'gc' }> | undefined),
  };
  // Deep-merge the AI Assistant config so a blob missing newer keys still
  // resolves complete (forward-compat), and clamp an unknown/stale model id
  // back to the default so the API never sends an unallowlisted model.
  out.assistant = {
    ...ASSISTANT_CONFIG_DEFAULT,
    ...(out.assistant as Partial<AssistantConfig> | undefined),
  };
  if (!(ASSISTANT_MODELS as readonly string[]).includes(out.assistant.model)) {
    out.assistant.model = ASSISTANT_CONFIG_DEFAULT.model;
  }
  return out;
}
